import { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { ReactComponent as EmailMethodIcon } from 'assets/iconButtons/method-email.svg';
import { ReactComponent as SmsMethodIcon } from 'assets/iconButtons/method-sms.svg';
import { ReactComponent as WhatsAppMethodIcon } from 'assets/iconButtons/method-whatsapp.svg';
import { ReactComponent as GoogleAuthMethodIcon } from 'assets/iconButtons/method-google-auth.svg';
import { ReactComponent as PushMethodIcon } from 'assets/iconButtons/method-push.svg';
import {
  CONFIRMATION_SEND_LIMIT_EXCEEDED_ERROR_CODE,
  PAID_CONFIRMATION_TYPES,
  TWO_FACTOR_AUTH_TYPES
} from 'components/common/constants';
import i18nContext from 'components/i18n-context';
import Button from 'uikit/Button/Button';
import './SwitchConfirmationMethodScreen.scss';

// Order + icon per the TRN-7254 Figma frame.
const METHODS = [
  { type: TWO_FACTOR_AUTH_TYPES.EMAIL, Icon: EmailMethodIcon, labelKey: 'switchConfirmationMethodScreen.method.EMAIL' },
  { type: TWO_FACTOR_AUTH_TYPES.SMS, Icon: SmsMethodIcon, labelKey: 'switchConfirmationMethodScreen.method.SMS' },
  {
    type: TWO_FACTOR_AUTH_TYPES.WHATSAPP,
    Icon: WhatsAppMethodIcon,
    labelKey: 'switchConfirmationMethodScreen.method.WHATSAPP'
  },
  {
    type: TWO_FACTOR_AUTH_TYPES.GOOGLE_AUTH,
    Icon: GoogleAuthMethodIcon,
    labelKey: 'switchConfirmationMethodScreen.method.GOOGLE_AUTH'
  },
  {
    type: TWO_FACTOR_AUTH_TYPES.PUSH_NOTIFICATION,
    Icon: PushMethodIcon,
    labelKey: 'switchConfirmationMethodScreen.method.PUSH_NOTIFICATION'
  }
];

// TRN-7406: methods the backend currently refuses — paid SMS / WhatsApp once the send limit is reached.
export const getUnavailableConfirmationTypes = (error) =>
  error?.code === CONFIRMATION_SEND_LIMIT_EXCEEDED_ERROR_CODE ? PAID_CONFIRMATION_TYPES : [];

// TRN-7254/TRN-7406: the methods this screen lists — every one it can show, except the current method, the
// ones the user doesn't have and the ones the backend currently refuses (unavailableConfirmationTypes).
const isSwitchableMethod = (type, currentConfirmationType, availableConfirmationTypes, unavailableConfirmationTypes) =>
  type !== currentConfirmationType &&
  availableConfirmationTypes?.includes(type) &&
  !unavailableConfirmationTypes?.includes(type);

// TRN-7491: "Try another method" opens this screen, so callers show that link only when this returns true —
// otherwise it opens a picker with nothing to pick (e.g. a V2 email-only user confirming by EMAIL has no
// available_confirmation_types at all; BIOMETRICS/PASSCODE from the mobile app are never listed here either).
export const hasSwitchableConfirmationMethod = (
  currentConfirmationType,
  availableConfirmationTypes,
  unavailableConfirmationTypes = []
) =>
  METHODS.some(({ type }) =>
    isSwitchableMethod(type, currentConfirmationType, availableConfirmationTypes, unavailableConfirmationTypes)
  );

const RadioIcon = ({ selected }) =>
  selected ? (
    <svg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M10 0.75C15.1086 0.75 19.25 4.89137 19.25 10C19.25 15.1086 15.1086 19.25 10 19.25C4.89137 19.25 0.75 15.1086 0.75 10C0.75 4.89137 4.89137 0.75 10 0.75Z'
        stroke='var(--element-graphic-accent)'
        strokeWidth='1.5'
      />
      <circle cx='10' cy='10' r='6' fill='var(--element-graphic-accent)' />
    </svg>
  ) : (
    <svg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M10 0.75C15.1086 0.75 19.25 4.89137 19.25 10C19.25 15.1086 15.1086 19.25 10 19.25C4.89137 19.25 0.75 15.1086 0.75 10C0.75 4.89137 4.89137 0.75 10 0.75Z'
        stroke='var(--element-border-default)'
        strokeWidth='1.5'
      />
    </svg>
  );

RadioIcon.propTypes = { selected: PropTypes.bool };

/**
 * TRN-7254 redesign. Behavior change from the previous version: rows used to switch the
 * confirmation method immediately on click; the new Figma shows radio buttons + a separate
 * "Continue" button, i.e. select-then-confirm. onSwitchMethod/onBack keep the same signatures as
 * before (still store-agnostic) — only *when* onSwitchMethod fires changes, so every existing
 * consumer (ActionConfirmation, PopUpInternalActionConfirmationScheme,
 * PopUpCurrencyExchangePaymentConfirmationScheme) keeps working unmodified.
 */
const SwitchConfirmationMethodScreen = ({
  onSwitchMethod,
  onBack,
  currentConfirmationType,
  availableConfirmationTypes,
  unavailableConfirmationTypes = [],
  onSwitchingChange
}) => {
  const i18n = useContext(i18nContext);
  const [isSwitching, setIsSwitching] = useState(false);

  // TRN-7406: unavailableConfirmationTypes — methods the backend currently refuses (paid SMS /
  // WhatsApp once the send limit is reached); hidden so the customer can't pick a dead end.
  const availableMethods = METHODS.filter(({ type }) =>
    isSwitchableMethod(type, currentConfirmationType, availableConfirmationTypes, unavailableConfirmationTypes)
  );

  const [selectedMethod, setSelectedMethod] = useState(availableMethods[0]?.type);

  // availableConfirmationTypes can arrive/change after mount — re-preselect whenever the
  // currently selected method is no longer (or not yet) one of the available ones, instead of
  // only picking a default once via the useState initializer above.
  useEffect(() => {
    if (!availableMethods.some(({ type }) => type === selectedMethod)) {
      setSelectedMethod(availableMethods[0]?.type);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableConfirmationTypes, unavailableConfirmationTypes, currentConfirmationType]);

  const handleContinue = async () => {
    if (!selectedMethod || isSwitching) return;
    setIsSwitching(true);
    onSwitchingChange?.(true);
    await onSwitchMethod(selectedMethod);
  };

  return (
    <>
      <div className={'confirmation-modal__header confirmation-modal__header--centered'}>
        <h2 className={'confirmation-modal__title'}>{i18n.getMessage('switchConfirmationMethodScreen.title')}</h2>
      </div>
      <div className={'confirmation-modal__body'}>
        <ul className={'switch-method-list'}>
          {availableMethods.map(({ type, Icon, labelKey }) => (
            <li key={type}>
              <button
                type={'button'}
                className={'switch-method-list-item'}
                onClick={() => setSelectedMethod(type)}
                disabled={isSwitching}
              >
                <span className={'switch-method-list-item__icon'}>
                  <Icon />
                </span>
                <span className={'switch-method-list-item__label'}>{i18n.getMessage(labelKey)}</span>
                <RadioIcon selected={selectedMethod === type} />
              </button>
            </li>
          ))}
        </ul>
        <div className={'switch-method-footer'}>
          <Button type={'outline'} size={'large'} fullWidth onClick={onBack} isDisabled={isSwitching}>
            {i18n.getMessage('switchConfirmationMethodScreen.btn.close')}
          </Button>
          <Button
            type={'primary'}
            size={'large'}
            fullWidth
            onClick={handleContinue}
            isDisabled={!selectedMethod || isSwitching}
          >
            {i18n.getMessage('switchConfirmationMethodScreen.btn.continue')}
          </Button>
        </div>
      </div>
    </>
  );
};

SwitchConfirmationMethodScreen.propTypes = {
  onSwitchMethod: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  currentConfirmationType: PropTypes.string,
  availableConfirmationTypes: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  unavailableConfirmationTypes: PropTypes.array,
  onSwitchingChange: PropTypes.func
};

export default observer(SwitchConfirmationMethodScreen);
